import React, { memo, useEffect, useMemo, useState } from "react";
import { format, getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { Image as ImageComponent } from "@swiggy-private/dls-web-core/dist/components/Image";
import {
    ALT_TEXTS,
    CURTAIN_RAISER,
    DATA_TEST_IDS,
    IMAGES,
    SIZE,
    TEXTS,
    TRUNCATE_LINE,
} from "./constant";
import {
    AboutSectionContainer,
    AboutSectionText,
    AboutSectionTitle,
    AboutSectionTitleContainer,
    ArtDecoRight,
    ContentContainer,
    CTAContainer,
    CTAText,
    CurtainRaiserContainer,
    ImageBackground,
    LogoContainer,
    RestaurantContainer,
    ConsentContainer,
    ConsentText,
    RestaurantTagText,
    RestaurantText,
    SwipeImage,
    UserText,
} from "./styled";
import { ICurtainRaiserProps } from "./types";
import { TermsAndConditionLinks } from "../Menu/components/TermsAndConditionLinks";

const CurtainRaiser = ({
    bgImage,
    logo,
    userName,
    restaurantText,
    restaurantTagText,
    aboutTitle,
    aboutText,
    ctaText,
    swipe,
    onSwipe,
}: ICurtainRaiserProps) => {
    const UserNameString = format(TEXTS.USER_TEXT, userName);
    const [isLoaded, setIsLoaded] = useState(false);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchEndY, setTouchEndY] = useState<number | null>(null);
    const [translateYPosition, setTranslateYPosition] = useState(0);

    // Minimum swipe distance in pixels
    const swipeThreshold = 30;

    const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
        setTouchStartY(e.targetTouches[0]?.clientY);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLButtonElement>) => {
        setTranslateYPosition(
            touchStartY && touchEndY && touchEndY - touchStartY < 0
                ? Math.round(touchEndY - touchStartY)
                : 0
        );
        setTouchEndY(e.targetTouches[0]?.clientY);
    };

    const handleTouchEnd = () => {
        if (!touchStartY || !touchEndY) return;

        const swipeDistance = touchEndY - touchStartY;

        if (swipeDistance < -swipeThreshold) {
            onSwipe();
        }

        setTranslateYPosition(0);
        setTouchStartY(null);
        setTouchEndY(null);
    };

    const logoUrl = useMemo(() => getCloudinaryUrl(logo), [logo]);

    const bgImageUrl = useMemo(
        () => getCloudinaryUrl(bgImage, { width: 400 * 1.5, height: 800 * 1.5 }) || "",
        [bgImage]
    );

    useEffect(() => {
        if (bgImageUrl) {
            const img = new Image();
            img.src = bgImageUrl;
            img.onload = () => setIsLoaded(true);
        }
    }, [bgImageUrl]);

    const consentLinksProps = {
        link1Text: CURTAIN_RAISER.CONSENT_LINK_TEXT,
        link1Url: CURTAIN_RAISER.CONSENT_LINK_URL,
        link2Text: CURTAIN_RAISER.CONSENT_LINK_TEXT_2,
        link2Url: CURTAIN_RAISER.CONSENT_LINK_URL_2,
        separatorText: " and ",
        variant: "V2.overline.neutral.medium" as const,
        color: "text_inverse_disabled" as const,
        anchorColor: "text_inverse_disabled" as const,
        anchorTextDecorationColor: "text_inverse_disabled" as const,
        inline: true,
        gap: "xxxSmall" as const,
    };

    return (
        <CurtainRaiserContainer isSwipeUp={swipe} translateYPosition={translateYPosition}>
            <ImageBackground imgUrl={bgImageUrl} isLoaded={isLoaded} />
            <ContentContainer
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <RestaurantContainer>
                    {!isEmpty(logo) && (
                        <LogoContainer>
                            <ImageComponent
                                src={logoUrl}
                                alt={ALT_TEXTS.LOGO}
                                height={SIZE.LOGO}
                                width={SIZE.LOGO}
                            />
                        </LogoContainer>
                    )}
                    {!isEmpty(userName) && (
                        <UserText variant="V2.overline.neutral.bold_spaced">
                            {UserNameString.toUpperCase()}
                        </UserText>
                    )}
                    <RestaurantText
                        maxLines={TRUNCATE_LINE.SMALL}
                        variant="V2.heading1.neutral.black"
                    >
                        {restaurantText}
                    </RestaurantText>
                    {!isEmpty(restaurantTagText) && (
                        <RestaurantTagText variant="V2.body2.neutral.regular">
                            {restaurantTagText}
                        </RestaurantTagText>
                    )}
                </RestaurantContainer>
                {!isEmpty(aboutText) && (
                    <AboutSectionContainer>
                        <AboutSectionTitleContainer>
                            <ImageComponent
                                src={IMAGES.ART_DECO}
                                alt={ALT_TEXTS.DECO}
                                height={SIZE.MEDIUM}
                                width={SIZE.X_LARGE}
                            />
                            <AboutSectionTitle variant="V2.overline.neutral.bold">
                                {aboutTitle || "About"}
                            </AboutSectionTitle>
                            <ArtDecoRight
                                src={IMAGES.ART_DECO}
                                alt={ALT_TEXTS.DECO}
                                height={SIZE.MEDIUM}
                                width={SIZE.X_LARGE}
                            />
                        </AboutSectionTitleContainer>
                        <AboutSectionText maxLines={10} variant="V2.body2.neutral.regular">
                            {aboutText}
                        </AboutSectionText>
                    </AboutSectionContainer>
                )}
                <CTAContainer
                    data-testId={DATA_TEST_IDS.VIEW_MENU_CTA}
                    buttonSize="medium"
                    color="primary"
                >
                    <CTAText variant="V2.subheading1.neutral.bold">{ctaText}</CTAText>
                    <SwipeImage
                        src={IMAGES.SWIPE}
                        alt={ALT_TEXTS.SWIPE}
                        height="18px"
                        width="12px"
                    />
                </CTAContainer>
                <ConsentContainer>
                    <ConsentText variant="V2.overline.neutral.medium">
                        {CURTAIN_RAISER.CONSENT_TEXT}{" "}
                        <TermsAndConditionLinks {...consentLinksProps} />
                    </ConsentText>
                </ConsentContainer>
            </ContentContainer>
        </CurtainRaiserContainer>
    );
};

export default memo(CurtainRaiser);
